import { formatFullName } from '@formatters/fullName.formatter';
import backendHttpClient from '@services/_httpClient/backend.http-client';

import type { CreateAdminPayload } from './dtos/create.dto';
import type { GetAllAdminsParams } from './dtos/getAll.dto';
import type { UpdateAdminDto, UpdateAdminPayload } from './dtos/update.dto';
import type { AdminRepository } from './repository';
import type {
  CreateAdminOutput,
  CreateAdminResponse,
} from './types/create.type';
import type {
  GetAllAdminsOutput,
  GetAllAdminsResponse,
} from './types/getAll.type';
import type {
  GetAllAdminsToSelectOutput,
  GetAllAdminsToSelectResponse,
} from './types/getAllToSelect.type';
import type {
  GetAdminByIdOutput,
  GetAdminByIdResponse,
} from './types/getById.type';
import type {
  UpdateAdminOutput,
  UpdateAdminResponse,
} from './types/update.type';

class AdminService implements AdminRepository {
  public async archive(id: string): Promise<void> {
    await backendHttpClient.delete<void>({
      path: `admins/${id}`,
    });
  }

  public async create(payload: CreateAdminPayload): Promise<CreateAdminOutput> {
    const { data } = await backendHttpClient.post<
      CreateAdminResponse,
      CreateAdminPayload
    >({
      body: payload,
      path: 'admins',
    });

    return { admin: data.admin };
  }

  public async getAll(params: GetAllAdminsParams): Promise<GetAllAdminsOutput> {
    const { data } = await backendHttpClient.get<
      GetAllAdminsResponse,
      GetAllAdminsParams
    >({
      params,
      path: 'admins',
    });

    return {
      count: data.count,
      list: data.list,
      pagination: data.pagination,
    };
  }

  public async getById(id: string): Promise<GetAdminByIdOutput> {
    const { data } = await backendHttpClient.get<GetAdminByIdResponse>({
      path: `admins/${id}`,
    });

    return { admin: data.admin };
  }

  public async getAllToSelect(): Promise<GetAllAdminsToSelectOutput> {
    const { data } = await backendHttpClient.get<GetAllAdminsToSelectResponse>({
      path: 'admins/select',
    });

    const output: GetAllAdminsToSelectOutput = {
      list: data.list.map(admin => ({
        label: formatFullName({
          firstName: admin.firstName,
          lastName: admin.lastName,
        }),
        value: admin.id,
      })),
    };

    return output;
  }

  public async unarchive(id: string): Promise<void> {
    await backendHttpClient.patch<void>({
      path: `admins/${id}/unarchive`,
    });
  }

  public async update({
    body,
    id,
  }: UpdateAdminPayload): Promise<UpdateAdminOutput> {
    const { data } = await backendHttpClient.put<
      UpdateAdminResponse,
      UpdateAdminDto
    >({
      body,
      path: `admins/${id}`,
    });

    return { admin: data.admin };
  }
}

export const adminService = new AdminService();
